/**
 * Feeds the inbound-MIDI store from the transport sink.
 *
 * This is the seam `midi/inbound.ts` was built for and its doc comment
 * predicted: *"whoever wires that in only needs to call `recordMidiIn()` per
 * event, no consumer-side change required."* This module is that wiring, and
 * it is the thing that makes StatusLed (DEC-041) actually light for the first
 * time since `packages/bridge/` was deleted 2026-08-20.
 *
 * It subscribes to `onMidiIn()`, so it is transport-agnostic by construction:
 * events arrive identically whether the transitional `/midi/recent` poll or
 * OPBridge's eventual `/ws/consumer` `midi_in` push produced them. Retiring
 * the poll changes nothing here.
 *
 * ⚠️ CHANNEL BASE — verified, not assumed. `StatusLedItem.channel` is
 * documented as "MIDI channel 0-15" (`layout/projectStore.ts`) and OPBridge
 * reports the raw 0-based channel nibble, so the two agree and NO conversion
 * happens here. This project has an established inconsistency where some item
 * kinds store 1-based channels (`feedback_item_channel_base_inconsistency`),
 * so the check was made explicitly rather than inherited: adding a +1/-1 here
 * "to be safe" would break a binding that currently matches.
 *
 * ⚠️ VOCABULARY — the two `MidiInEvent` types are NOT the same type. The
 * transport contract uses OPBridge's `note_on`/`note_off` (snake, matching
 * their wire); the store predates it and uses `noteon`/`noteoff`. They are
 * mapped here deliberately rather than by renaming either side: the transport
 * shape is OPBridge's to define, and the store shape is what `StatusLed.tsx`
 * already matches against.
 */

import { createEffect, createMemo, onCleanup } from 'solid-js';
import { onMidiIn, type MidiInEvent as TransportMidiInEvent } from '../opbridge/consumerWs.ts';
import { activeProject } from '../layout/projectStore.ts';
import { recordMidiIn, type MidiInEvent as StoreMidiInEvent } from './inbound.ts';

/** Map the transport vocabulary onto the store's. Returns null for kinds the
 *  store does not model (`pc`, and anything OPBridge adds later) so an
 *  unmodelled kind is dropped rather than coerced into a note or a CC. */
function storeKind(kind: string): StoreMidiInEvent['kind'] | null {
  switch (kind) {
    case 'cc':       return 'cc';
    case 'note_on':  return 'noteon';
    case 'note_off': return 'noteoff';
    default:         return null;
  }
}

function forward(e: TransportMidiInEvent): void {
  const kind = storeKind(e.kind);
  if (kind === null) return;
  recordMidiIn({ kind, number: e.number, value: e.value, channel: e.channel });
}

/** Item kinds that actually consume inbound MIDI. Today only StatusLed
 *  (DEC-041) reads `midi/inbound.ts`. MIDI-triggered buttons do NOT belong
 *  here: their A-path runs entirely inside OPBridge's rules engine and never
 *  touches this store. Add a kind here when it starts reading the store, not
 *  when it merely mentions MIDI. */
const INBOUND_CONSUMER_KINDS = new Set<string>(['statusLed']);

/** Does the ACTIVE PROJECT contain anything that consumes inbound MIDI?
 *  Whole-project, not just the active page: a StatusLed two pages away is
 *  still live state the operator may switch to mid-set, and dropping the feed
 *  on a page switch would make the lamp wrong exactly when they looked at it. */
function projectNeedsInboundMidi(): boolean {
  const p = activeProject();
  if (!p) return false;
  return p.pages.some((pg) => pg.items.some((it) => INBOUND_CONSUMER_KINDS.has(it.kind)));
}

/**
 * Start feeding the store, but ONLY while the project has a consumer.
 *
 * ⚠️ This gate is load-bearing, not an optimisation. Subscribing counts as a
 * real consumer, and `midiInPoll` starts draining as soon as
 * `midiInListenerCount()` is non-zero. `GET /midi/recent` is a DESTRUCTIVE
 * drain, so an unconditional subscription would make the app poll 10x/sec
 * forever and swallow every inbound message - stealing them from any other
 * consumer - even for the overwhelmingly common project that contains no
 * StatusLed at all. It would also spend LAN and battery on the gig iPad for
 * data nothing renders.
 *
 * (Caught by `midi_in_poll_delivers_real_wire` regressing the moment this
 * feed was first wired unconditionally - the guard it asserts had quietly
 * stopped being reachable in production.)
 *
 * Value-gated through a memo so ordinary project edits (moving an item,
 * renaming a page) do not churn the subscription; only the boolean flipping
 * re-runs the effect. Must be called from a reactive owner (App's setup), so
 * the effect and its `onCleanup` are disposed with the app.
 */
export function startInboundMidiFeed(): void {
  const needed = createMemo(projectNeedsInboundMidi);
  createEffect(() => {
    if (!needed()) return;
    const off = onMidiIn(forward);
    onCleanup(off);
  });
}
